/**
 * Client-side withdraw orchestration.
 *
 * The user holds the secret (nullifier+secret in the note). All proof
 * generation happens here, on the user's machine. The output is a
 * broadcast-only payload that the relayer can submit on-chain but cannot
 * tamper with.
 *
 * Two ways to source the Merkle tree leaves:
 *   1. From the user's own TON client (max privacy; slow on big pools).
 *   2. From a relayer's `GET /tree/commitments` endpoint (faster; the
 *      relayer learns *which pool* you're using but not which leaf you
 *      withdraw — your specific commitment is never sent over the wire,
 *      only the unordered leaf set is).
 *
 * Either way, the proof is generated client-side.
 */
import { Address, toNano } from "@ton/core";
import axios from "axios";
import { TonadoPool, buildWithdrawBody } from "./pool";
import { getPool } from "./deployments";
import { parseNote } from "./notes";
import { addressToFieldElement } from "./tonClient";
import { buildMerkleTree, merkleProofForLeaf } from "./merkleTree";
import { scanPoolTransactions } from "./indexer";
import { generateProof } from "./proof";
/** Build a withdraw proof entirely locally. */
export async function buildWithdrawArgs(opts) {
    const parsed = await parseNote(opts.note);
    const network = opts.network ?? parsed.network;
    const pool = getPool(network, parsed.assetId, parsed.amount);
    if (!pool || !pool.address) {
        throw new Error(`No deployed pool for ${parsed.assetId}-${parsed.amount} on ${network}. ` +
            `Make sure the note's network matches the active environment.`);
    }
    // Load commitments to rebuild the tree locally. Pick the source in
    // priority order; only throw when NONE of the three options were
    // supplied. The previous implementation built `defaultSource` eagerly
    // via an IIFE, so the "no source" throw fired even when
    // `preloadedCommitments` made the source irrelevant.
    let commitments;
    if (opts.preloadedCommitments) {
        commitments = opts.preloadedCommitments;
    }
    else if (opts.commitmentSource) {
        commitments = await loadCommitments(opts.commitmentSource, pool.address);
    }
    else if (opts.client) {
        commitments = await loadCommitments({ type: "ton-client", client: opts.client }, pool.address);
    }
    else {
        throw new Error("Either pass `client`, `commitmentSource`, or `preloadedCommitments` — no source for the merkle leaves was provided.");
    }
    const tree = await buildMerkleTree({ leaves: commitments });
    const merkle = await merkleProofForLeaf(tree, parsed.deposit.commitment);
    // Optional fail-fast: when a TonClient is available, cross-check our
    // root against the live on-chain history before paying the prover
    // cost. Browser callers (which can't construct a TonClient — no API
    // key) skip this; the pool's `ERR_UNKNOWN_ROOT` still catches a
    // stale root at withdraw time.
    if (opts.client) {
        const onchain = TonadoPool.at(Address.parse(pool.address));
        const provider = opts.client.provider(onchain.address);
        const known = await onchain.isKnownRoot(provider, merkle.root);
        if (!known) {
            throw new Error(`Computed merkle root not in on-chain known-roots set. ` +
                `Re-sync your local commitments; possible causes: stale commitment list, ` +
                `pool changed, hash mismatch between client and contract.`);
        }
    }
    // Build the witness and run the prover. This is the CPU-heavy step (~5–15 s).
    const inputs = {
        root: merkle.root,
        nullifierHash: parsed.deposit.nullifierHash,
        recipient: addressToFieldElement(opts.recipient),
        relayer: addressToFieldElement(opts.relayer),
        fee: opts.fee,
        refund: opts.refund ?? 0n,
        nullifier: parsed.deposit.nullifier,
        secret: parsed.deposit.secret,
        pathElements: merkle.pathElements,
        pathIndices: merkle.pathIndices,
    };
    const { proof, publicSignals } = await generateProof(inputs);
    return {
        proof,
        publicSignals,
        root: merkle.root,
        nullifierHash: parsed.deposit.nullifierHash,
        recipient: opts.recipient,
        relayer: opts.relayer,
        fee: opts.fee,
        refund: opts.refund ?? 0n,
    };
}
export function buildBroadcastPayload(args) {
    const w = args.args;
    return {
        pool: args.pool,
        proof: w.proof,
        publicSignals: w.publicSignals.slice(0, 6),
        recipient: w.recipient.toString(),
        fee: w.fee.toString(),
        refund: w.refund.toString(),
    };
}
/**
 * Build the raw withdraw message body — used by the `withdraw self` flow
 * (the CLI submits its own proof directly to the pool, no relayer
 * involved).
 */
export async function buildWithdrawMessageBody(args) {
    return buildWithdrawBody({
        queryId: 0n,
        proof: args.proof,
        root: args.root,
        nullifierHash: args.nullifierHash,
        recipient: args.recipient,
        relayer: args.relayer,
        fee: args.fee,
        refund: args.refund,
    });
}
export const DEFAULT_WITHDRAW_VALUE = toNano("0.2");
async function loadCommitments(source, poolAddress) {
    if (source.type === "static")
        return source.commitments;
    if (source.type === "relayer")
        return loadCommitmentsFromRelayer(source.relayerUrl, poolAddress);
    return loadCommitmentsFromChain(source.client, poolAddress);
}
/**
 * Pull commitments from a relayer's `GET /tree/commitments` endpoint
 * (paginated). The relayer sees that you're building the tree for this
 * pool, but NOT which commitment is yours.
 */
export async function loadCommitmentsFromRelayer(relayerUrl, poolAddress) {
    const out = [];
    let from = 0;
    while (true) {
        const resp = await axios.get(`${relayerUrl}/tree/commitments`, {
            params: { pool: poolAddress, from, limit: 5000 },
            timeout: 60_000,
        });
        for (const c of resp.data.commitments)
            out.push(BigInt(c.commitment));
        if (resp.data.nextFrom == null)
            break;
        from = resp.data.nextFrom;
    }
    return out;
}
async function loadCommitmentsFromChain(client, poolAddress) {
    // Parallel to depositFlow.ts :: loadAllCommitments. Do NOT early-break
    // on a page with zero deposit events — admin/withdraw/refund txs can
    // fill a page entirely while older pages still hold deposits. Sort the
    // final list by leafIndex so we don't depend on scan-order assumptions.
    const collected = [];
    let cursor = {};
    while (true) {
        const { events, lastLt, lastHash } = await scanPoolTransactions(client, {
            pool: Address.parse(poolAddress),
            maxTransactions: 256,
            fromLt: cursor.lt,
            fromHash: cursor.hash,
        });
        for (const e of events) {
            collected.push({ commitment: e.commitment, leafIndex: e.leafIndex });
        }
        if (!lastLt || (cursor.lt !== undefined && lastLt === cursor.lt))
            break;
        cursor = { lt: lastLt, hash: lastHash };
    }
    collected.sort((a, b) => a.leafIndex - b.leafIndex);
    return collected.map((e) => e.commitment);
}
//# sourceMappingURL=withdrawFlow.js.map